// Tiny, dependency-free arithmetic evaluator for the quick calculator.
//
// Supports +, -, *, /, parentheses, decimal numbers and unary minus.
// It never uses eval/Function/RegExp on raw input: the expression is
// tokenized character-by-character and any unexpected character throws.
// Throws on malformed input and division by zero so callers can render
// an error state.

const OPERATORS = {
  "+": { precedence: 1, apply: (a, b) => a + b },
  "-": { precedence: 1, apply: (a, b) => a - b },
  "*": { precedence: 2, apply: (a, b) => a * b },
  "/": {
    precedence: 2,
    apply: (a, b) => {
      if (b === 0) throw new Error("Division by zero");
      return a / b;
    },
  },
};

function tokenize(expr) {
  const tokens = [];
  let i = 0;

  while (i < expr.length) {
    const char = expr[i];

    if (char === " " || char === "\t") {
      i += 1;
      continue;
    }

    if ((char >= "0" && char <= "9") || char === ".") {
      let number = "";
      let dotSeen = false;
      while (i < expr.length && ((expr[i] >= "0" && expr[i] <= "9") || expr[i] === ".")) {
        if (expr[i] === ".") {
          if (dotSeen) throw new Error("Invalid number");
          dotSeen = true;
        }
        number += expr[i];
        i += 1;
      }
      if (number === ".") throw new Error("Invalid number");
      tokens.push({ type: "number", value: parseFloat(number) });
      continue;
    }

    if (char === "+" || char === "-" || char === "*" || char === "/") {
      tokens.push({ type: "operator", value: char });
      i += 1;
      continue;
    }

    if (char === "(" || char === ")") {
      tokens.push({ type: "paren", value: char });
      i += 1;
      continue;
    }

    throw new Error(`Unexpected character: ${char}`);
  }

  return tokens;
}

// Shunting-yard → RPN, resolving unary minus/plus along the way.
function toRpn(tokens) {
  const output = [];
  const operators = [];
  let prev = null; // previous emitted token, to detect unary context

  for (const token of tokens) {
    if (token.type === "number") {
      output.push(token);
    } else if (token.type === "operator") {
      const isUnary =
        prev === null ||
        (prev.type === "operator") ||
        (prev.type === "paren" && prev.value === "(");

      if (isUnary) {
        if (token.value === "-") {
          // Represent unary minus as (0 - x) by injecting a leading zero.
          output.push({ type: "number", value: 0 });
          operators.push({ type: "operator", value: "-" });
        } else if (token.value === "+") {
          // Unary plus is a no-op.
        } else {
          throw new Error("Unexpected operator");
        }
      } else {
        while (
          operators.length &&
          operators[operators.length - 1].type === "operator" &&
          OPERATORS[operators[operators.length - 1].value].precedence >=
            OPERATORS[token.value].precedence
        ) {
          output.push(operators.pop());
        }
        operators.push(token);
      }
    } else if (token.value === "(") {
      operators.push(token);
    } else {
      // Closing paren.
      while (operators.length && operators[operators.length - 1].value !== "(") {
        output.push(operators.pop());
      }
      if (!operators.length) throw new Error("Mismatched parentheses");
      operators.pop(); // discard "("
    }
    prev = token;
  }

  while (operators.length) {
    const op = operators.pop();
    if (op.type === "paren") throw new Error("Mismatched parentheses");
    output.push(op);
  }

  return output;
}

function evalRpn(rpn) {
  const stack = [];

  for (const token of rpn) {
    if (token.type === "number") {
      stack.push(token.value);
    } else {
      const b = stack.pop();
      const a = stack.pop();
      if (a === undefined || b === undefined) throw new Error("Invalid expression");
      stack.push(OPERATORS[token.value].apply(a, b));
    }
  }

  if (stack.length !== 1) throw new Error("Invalid expression");
  const result = stack[0];
  if (!Number.isFinite(result)) throw new Error("Invalid result");
  return result;
}

// Evaluate an arithmetic expression and return a JS Number.
// Throws on malformed input or division by zero.
export function evaluate(expr) {
  if (typeof expr !== "string") throw new Error("Expression must be a string");
  return evalRpn(toRpn(tokenize(expr)));
}
